import React, { useState, useEffect, useRef } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import Swal from "sweetalert2";
import PopupMessage from "../../components/common/PopUpMessage";
import "../../assets/css/GmailSetup.css";
import EmailService from "../../services/EmailService";
import LeadService from "../../services/LeadService";

const SWEET_ALERT_CONFIRM_COLOR = "rgb(208 125 63)";

const normalizeGroupSuggestions = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];

  if (!Array.isArray(payload)) return [];

  const groupsByKey = new Map();

  payload.forEach((group, index) => {
    const groupId =
      group?.groupId ??
      group?.GroupId ??
      group?.leadGroupId ??
      group?.LeadGroupId ??
      group?.id ??
      group?.Id ??
      "";
    const groupName =
      group?.groupName ??
      group?.GroupName ??
      group?.name ??
      group?.Name ??
      "";
    const leadItems =
      group?.associatedLeads ??
      group?.AssociatedLeads ??
      group?.leads ??
      group?.Leads ??
      group?.leadDetails ??
      group?.LeadDetails ??
      [];
    const normalizedLeads = (
      Array.isArray(leadItems) && leadItems.length > 0 ? leadItems : [group]
    )
      .map((lead, leadIndex) => ({
        leadId:
          lead?.leadId ??
          lead?.LeadId ??
          lead?.id ??
          lead?.Id ??
          `${groupId || groupName || index}-lead-${leadIndex}`,
        leadName:
          lead?.leadName ??
          lead?.LeadName ??
          lead?.name ??
          lead?.Name ??
          "",
        email: lead?.email ?? lead?.Email ?? "",
      }))
      .filter((lead) => lead.email);

    if (!groupName || normalizedLeads.length === 0) return;

    const groupKey = String(groupId || groupName);
    const existingGroup = groupsByKey.get(groupKey);

    if (existingGroup) {
      const existingEmails = new Set(
        existingGroup.leads.map((lead) => lead.email.toLowerCase()),
      );
      normalizedLeads.forEach((lead) => {
        const normalizedEmail = lead.email.toLowerCase();
        if (!existingEmails.has(normalizedEmail)) {
          existingGroup.leads.push(lead);
          existingEmails.add(normalizedEmail);
        }
      });
      return;
    }

    groupsByKey.set(groupKey, {
      groupId: groupId || groupKey,
      groupName,
      leads: normalizedLeads,
    });
  });

  return Array.from(groupsByKey.values());
};

const EmailSetup = () => {
  const emptySmtpForm = {
    smtpServer: "",
    senderEmail: "",
    smtpPort: "",
    userName: "",
    senderName: "",
    password: "",
  };

  const [form, setForm] = useState({
    ...emptySmtpForm,
  });

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [token, setToken] = useState(null);
  const [integratedProviders, setIntegratedProviders] = useState(new Set());

  const [compose, setCompose] = useState({
    to: "",
    subject: "",
    body: "",
  });
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [groupLeadSearch, setGroupLeadSearch] = useState("");
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionRequestIdRef = useRef(0);
  const recipientInputRef = useRef(null);

  // ✅ Common popup state
  const [popup, setPopup] = useState({ message: "", type: "success" });

  // ✅ On mount, check localStorage for Gmail token
  useEffect(() => {
    const token = EmailService.getStoredGmailToken();
    if (token) {
      setToken(token);
      setSelectedProvider("Gmail");
      setIntegratedProviders((prev) => new Set(prev).add("Gmail"));
    }
  }, []);

  useEffect(() => {
    const searchText = compose.to.trim();
    const requestId = suggestionRequestIdRef.current + 1;
    suggestionRequestIdRef.current = requestId;

    if (searchText.length < 3) {
      setGroupSuggestions([]);
      setShowSuggestions(false);
      setIsSuggestionLoading(false);
      return undefined;
    }

    setIsSuggestionLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await LeadService.getLeadAutoSuggestion(searchText);
        if (suggestionRequestIdRef.current !== requestId) return;

        const suggestions = normalizeGroupSuggestions(response);
        const selectedGroupIds = new Set(
          selectedGroups.map((group) => String(group.groupId)),
        );
        setGroupSuggestions(
          suggestions.filter(
            (suggestion) => !selectedGroupIds.has(String(suggestion.groupId)),
          ),
        );
        setShowSuggestions(true);
      } catch (error) {
        if (suggestionRequestIdRef.current !== requestId) return;

        console.error("Error fetching lead suggestions:", error);
        setGroupSuggestions([]);
        setShowSuggestions(false);
      } finally {
        if (suggestionRequestIdRef.current === requestId) {
          setIsSuggestionLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [compose.to, selectedGroups]);

  // ✅ Google login hook
  const google_login = useGoogleLogin({
    scope:
      "https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly",
    onSuccess: async (response) => {
      const emailAuth = {
        emailtype: "gmail",
        access_token: response.access_token,
      };
      localStorage.setItem("email_auth", JSON.stringify(emailAuth));
      setSelectedProvider("Gmail");
      setToken(response.access_token);
      setIntegratedProviders((prev) => new Set(prev).add("Gmail"));
      setPopup({ message: "Gmail integrated successfully!", type: "success" });

      try {
        const userInfo = await EmailService.getGoogleUserInfo(response.access_token);
        console.log("User Info:", userInfo.data);
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    },
    onError: (error) => {
      console.log("OAuth Error:", error);
      setPopup({ message: "Failed to integrate Gmail.", type: "error" });
    },
  });

  // ✅ Handle provider selection
  const handleProviderClick = (provider) => {
    setSelectedProvider(provider);

    // clear compose form whenever switching provider
    setCompose({ to: "", subject: "", body: "" });
    setSelectedGroups([]);
    setActiveGroupId(null);
    setGroupLeadSearch("");
    setGroupSuggestions([]);
    setShowSuggestions(false);

    if (provider === "Gmail") {
      if (token) {
        setPopup({
          message: "Gmail is already integrated and live.",
          type: "success",
        });
      } else {
        google_login();
      }
    } else if (provider === "Outlook") {
      setIntegratedProviders((prev) => new Set(prev).add(provider));
    }
  };

  const handleRemoveIntegration = async (e, provider) => {
    e.stopPropagation();

    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Remove Integration?",
      text: `Remove your ${provider} email integration? You can integrate a new email again after this.`,
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: SWEET_ALERT_CONFIRM_COLOR,
    });

    if (!confirmation.isConfirmed) return;

    if (provider === "Gmail") {
      EmailService.clearStoredEmailAuth();
      setToken(null);
    }

    if (provider === "Other") {
      setForm({ ...emptySmtpForm });
    }

    setIntegratedProviders((prev) => {
      const next = new Set(prev);
      next.delete(provider);
      return next;
    });

    if (selectedProvider === provider) {
      setSelectedProvider(null);
    }

    setCompose({ to: "", subject: "", body: "" });
    setSelectedGroups([]);
    setActiveGroupId(null);
    setGroupLeadSearch("");
    setGroupSuggestions([]);
    setShowSuggestions(false);

    setPopup({
      message: `${provider} integration removed successfully.`,
      type: "success",
    });
  };

  // ✅ Handle SMTP form changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRecipientChange = (e) => {
    setCompose({ ...compose, to: e.target.value });
    setActiveGroupId(null);
    setGroupLeadSearch("");
  };

  const handleGroupSelect = (group) => {
    const selectedLeadIds = new Set(
      group.leads.map((lead) => String(lead.leadId)),
    );

    setSelectedGroups((prev) => [
      ...prev,
      {
        ...group,
        selectedLeadIds,
      },
    ]);
    setCompose({ ...compose, to: "" });
    setGroupSuggestions([]);
    setShowSuggestions(false);
    setActiveGroupId(String(group.groupId));
    setGroupLeadSearch("");
  };

  const handleGroupRemove = (e, groupId) => {
    e.stopPropagation();
    setSelectedGroups((prev) =>
      prev.filter((group) => String(group.groupId) !== String(groupId)),
    );
    if (String(activeGroupId) === String(groupId)) {
      setActiveGroupId(null);
      setGroupLeadSearch("");
    }
  };

  const updateGroupLeadSelection = (groupId, getNextSelectedLeadIds) => {
    setSelectedGroups((prev) =>
      prev.map((group) => {
        if (String(group.groupId) !== String(groupId)) return group;

        return {
          ...group,
          selectedLeadIds: getNextSelectedLeadIds(group),
        };
      }),
    );
  };

  const handleLeadToggle = (groupId, leadId) => {
    updateGroupLeadSelection(groupId, (group) => {
      const nextSelectedLeadIds = new Set(group.selectedLeadIds);
      const normalizedLeadId = String(leadId);

      if (nextSelectedLeadIds.has(normalizedLeadId)) {
        nextSelectedLeadIds.delete(normalizedLeadId);
      } else {
        nextSelectedLeadIds.add(normalizedLeadId);
      }

      return nextSelectedLeadIds;
    });
  };

  const handleSelectAllLeads = (groupId) => {
    updateGroupLeadSelection(
      groupId,
      (group) => new Set(group.leads.map((lead) => String(lead.leadId))),
    );
  };

  const handleDeselectAllLeads = (groupId) => {
    updateGroupLeadSelection(groupId, () => new Set());
  };

  const resolveRecipientEmails = () => {
    const uniqueEmails = new Set();

    selectedGroups.forEach((group) => {
      group.leads.forEach((lead) => {
        if (
          lead.email &&
          group.selectedLeadIds.has(String(lead.leadId))
        ) {
          uniqueEmails.add(lead.email.trim().toLowerCase());
        }
      });
    });

    return Array.from(uniqueEmails);
  };

  // ✅ Handle SMTP form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("SMTP Credentials Submitted:", form);
    setIntegratedProviders((prev) => new Set(prev).add("Other"));
    setPopup({ message: "SMTP credentials saved!", type: "success" });
  };

  // ✅ Send Email function
  const handleSendEmail = async () => {
    const recipientEmails = resolveRecipientEmails();
    const recipientEmailText = recipientEmails.join(",");

    if (recipientEmails.length === 0 || !compose.subject || !compose.body) {
      setPopup({
        message: "Please fill in all fields before sending.",
        type: "error",
      });
      return;
    }

    if (selectedProvider === "Gmail") {
      if (!token) {
        setPopup({ message: "Please login with Gmail first.", type: "error" });
        return;
      }

      try {
        const response = await EmailService.sendGmail(
          token,
          recipientEmailText,
          compose.subject,
          compose.body
        );

        console.log("Email sent:", response.data);
        setPopup({ message: "Email sent successfully!", type: "success" });

        // ✅ clear form after send
        setCompose({ to: "", subject: "", body: "" });
        setSelectedGroups([]);
        setActiveGroupId(null);
        setGroupLeadSearch("");
      } catch (error) {
        console.error("Error sending email:", error);
        setPopup({ message: "Failed to send email.", type: "error" });
      }
    } else {
      // Placeholder for Outlook/Other send logic
      console.log("Send via:", selectedProvider, {
        ...compose,
        to: recipientEmailText,
      });
      setPopup({
        message: `${selectedProvider} email sent (mock)!`,
        type: "success",
      });

      // ✅ clear form after send
      setCompose({ to: "", subject: "", body: "" });
      setSelectedGroups([]);
      setActiveGroupId(null);
      setGroupLeadSearch("");
    }
  };

  const activeGroup = selectedGroups.find(
    (group) => String(group.groupId) === String(activeGroupId),
  );
  const filteredActiveGroupLeads = activeGroup
    ? activeGroup.leads.filter((lead) => {
        const searchText = groupLeadSearch.trim().toLowerCase();
        if (!searchText) return true;

        return (
          lead.leadName.toLowerCase().includes(searchText) ||
          lead.email.toLowerCase().includes(searchText)
        );
      })
    : [];

  return (
    <div className="mail-setup-wrapper">
      <h2 className="section-title">Integrate your mail</h2>
      <p className="section-subtitle">
        Send emails through your business email account to your leads.
      </p>

      {/* Mail Providers */}
      <div className="mail-providers">
        {["Gmail", "Outlook", "Other"].map((provider) => (
          <div
            className={`provider-item ${
              selectedProvider === provider ? "selected" : ""
            } ${integratedProviders.has(provider) ? "active-green" : ""}`}
            key={provider}
            onClick={() => handleProviderClick(provider)}
          >
            {integratedProviders.has(provider) && (
              <button
                type="button"
                className="remove-integration-badge"
                aria-label={`Remove ${provider} integration`}
                title={`Remove ${provider} integration`}
                onClick={(e) => handleRemoveIntegration(e, provider)}
              >
                ×
              </button>
            )}
            <div className="provider-circle">
              <img
                src={`../assets/images/${provider.toLowerCase()}.png`}
                alt={provider}
                style={{ maxHeight: "40px" }}
              />
            </div>
            <span>{provider}</span>
          </div>
        ))}
      </div>

      {/* SMTP Form (only for Other) */}
      {selectedProvider === "Other" && (
        <form className="smtp-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <input
              type="text"
              name="smtpServer"
              placeholder="SMTP server"
              value={form.smtpServer}
              onChange={handleChange}
            />
            <input
              type="email"
              name="senderEmail"
              placeholder="Sender email"
              value={form.senderEmail}
              onChange={handleChange}
            />
            <input
              type="text"
              name="smtpPort"
              placeholder="SMTP port"
              value={form.smtpPort}
              onChange={handleChange}
            />
            <input
              type="text"
              name="userName"
              placeholder="User name"
              value={form.userName}
              onChange={handleChange}
            />
            <input
              type="text"
              name="senderName"
              placeholder="Sender name"
              value={form.senderName}
              onChange={handleChange}
            />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
            />
          </div>
          <div className="form-submit">
            <button type="submit">Save Credentials</button>
          </div>
        </form>
      )}

      {/* ✅ Compose Email (shown for all providers) */}
      {selectedProvider && (
        <div className="compose-email-section">
          <div className="compose-header">
            <h3>Compose Emails ({selectedProvider})</h3>
            <button className="all-leads-btn">Send to All leads</button>
          </div>

          <div className="compose-box">
            <div className="compose-to">
              <label>To:</label>
              <div className="recipient-autocomplete">
                <div
                  className="recipient-token-input"
                  onClick={() => recipientInputRef.current?.focus()}
                >
                  {selectedGroups.map((group) => (
                    <button
                      type="button"
                      className={`recipient-group-token ${
                        String(activeGroupId) === String(group.groupId)
                          ? "active"
                          : ""
                      }`}
                      key={group.groupId}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveGroupId(
                          String(activeGroupId) === String(group.groupId)
                            ? null
                            : String(group.groupId),
                        );
                        setGroupLeadSearch("");
                      }}
                    >
                      <span>{group.groupName}</span>
                      <span className="recipient-group-token-count">
                        {group.selectedLeadIds.size}/{group.leads.length}
                      </span>
                      <span
                        className="recipient-group-token-remove"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleGroupRemove(e, group.groupId)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleGroupRemove(e, group.groupId);
                          }
                        }}
                      >
                        x
                      </span>
                    </button>
                  ))}

                  <input
                    ref={recipientInputRef}
                    className="recipient-search-input"
                    type="text"
                    placeholder={
                      selectedGroups.length > 0
                        ? "Add another group"
                        : "Search group"
                    }
                    value={compose.to}
                    onChange={handleRecipientChange}
                    onFocus={() => {
                      if (compose.to.trim().length >= 3) {
                        setShowSuggestions(true);
                      }
                    }}
                    autoComplete="off"
                  />
                </div>

                {showSuggestions &&
                  (groupSuggestions.length > 0 || isSuggestionLoading) && (
                    <div className="lead-suggestions-dropdown">
                      {isSuggestionLoading ? (
                        <div className="lead-suggestion-status">
                          Searching groups...
                        </div>
                      ) : (
                        groupSuggestions.map((suggestion) => (
                          <button
                            type="button"
                            className="lead-suggestion-item"
                            key={suggestion.groupId}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleGroupSelect(suggestion)}
                          >
                            <span className="lead-suggestion-name">
                              {suggestion.groupName}
                            </span>
                            <span className="lead-suggestion-email">
                              {suggestion.leads.length} leads
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                {activeGroup && (
                  <div className="group-leads-panel">
                    <div className="group-leads-panel-header">
                      <div>
                        <strong>{activeGroup.groupName}</strong>
                        <span>
                          {activeGroup.selectedLeadIds.size} of{" "}
                          {activeGroup.leads.length} selected
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveGroupId(null);
                          setGroupLeadSearch("");
                        }}
                      >
                        x
                      </button>
                    </div>

                    <input
                      className="group-lead-search"
                      type="text"
                      placeholder="Search leads"
                      value={groupLeadSearch}
                      onChange={(e) => setGroupLeadSearch(e.target.value)}
                    />

                    <div className="group-leads-actions">
                      <button
                        type="button"
                        onClick={() => handleSelectAllLeads(activeGroup.groupId)}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleDeselectAllLeads(activeGroup.groupId)
                        }
                      >
                        Deselect all
                      </button>
                    </div>

                    <div className="group-leads-list">
                      {filteredActiveGroupLeads.length > 0 ? (
                        filteredActiveGroupLeads.map((lead) => (
                          <label
                            className="group-lead-option"
                            key={`${activeGroup.groupId}-${lead.leadId}`}
                          >
                            <input
                              type="checkbox"
                              checked={activeGroup.selectedLeadIds.has(
                                String(lead.leadId),
                              )}
                              onChange={() =>
                                handleLeadToggle(
                                  activeGroup.groupId,
                                  lead.leadId,
                                )
                              }
                            />
                            <span>
                              <strong>{lead.leadName || lead.email}</strong>
                              <small>{lead.email}</small>
                            </span>
                          </label>
                        ))
                      ) : (
                        <div className="lead-suggestion-status">
                          No leads found.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="compose-to">
              <label>Subject:</label>
              <input
                type="text"
                placeholder="Enter subject"
                value={compose.subject}
                onChange={(e) =>
                  setCompose({ ...compose, subject: e.target.value })
                }
              />
            </div>

            <div className="compose-to">
              <label>Message:</label>
              <textarea
                rows="8"
                placeholder="Enter your message"
                value={compose.body}
                onChange={(e) =>
                  setCompose({ ...compose, body: e.target.value })
                }
              ></textarea>
            </div>

            <button className="send-btn" onClick={handleSendEmail}>
              Send
            </button>
          </div>
        </div>
      )}

      {/* ✅ Common PopupMessage */}
      <PopupMessage
        message={popup.message}
        type={popup.type}
        onClose={() => setPopup({ message: "", type: "success" })}
      />
    </div>
  );
};

export default EmailSetup;
